import type { IChallenge } from "@bbj-types/challenge";
import type { ISettingMaster } from "@bbj-types/setting";
import { IconSVG } from "@data-display/icon/IconSVG";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Alert, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import type { MouseEvent, ReactNode } from "react";
import { PaperCandidateChallengeActions } from "./actions";
import type { TPaperCandidateChallengeAction } from "./actions/types";
import { PaperCandidateChallengeEditForm } from "./edit";
import { CandidateChallengeLoadingState, usePaperCandidateChallenge } from "./logics";
import { PaperCandidateObjective } from "./objective";
import { PaperCreateCandidateObjective } from "./objective/create";
import { PaperCandidateChallengeRoot, paperCandidateChallengeClasses } from "./styles";
import type { TPaperCandidateChallengeChallenge, TPaperCandidateChallengeObjective } from "./types";

export interface IPaperCandidateChallengeProps<
  CandidateObjective extends TPaperCandidateChallengeObjective,
  CandidateChallenge extends TPaperCandidateChallengeChallenge<CandidateObjective>,
  Action extends TPaperCandidateChallengeAction,
> {
  challenges: IChallenge[];
  candidateChallenge: CandidateChallenge;
  additionalChallengeActions?: ReactNode;
  onDeleteChallenge?: (candidateChallenge: CandidateChallenge) => void | Promise<void>;
  onEditChallenge?: (candidateChallenge: CandidateChallenge) => void | Promise<void>;
  onCreateObjective?: (CandidateObjective: TPaperCandidateChallengeObjective) => void | Promise<void>;
  onEditObjective?: (candidateObjective: CandidateObjective, index: number) => void | Promise<void>;
  onDeleteObjective?: (candidateObjective: CandidateObjective, index: number) => void | Promise<void>;
  actions?: Action[];
  actionsLoading?: boolean;
  actionTypes?: ISettingMaster["values"];
  actionChannels?: ISettingMaster["values"];
  onActionClick?: (event: MouseEvent, action: Action) => void;
  disableCreateObjective?: boolean;
}

export const PaperCandidateChallenge = <
  CandidateObjective extends TPaperCandidateChallengeObjective,
  CandidateChallenge extends TPaperCandidateChallengeChallenge<CandidateObjective>,
  Action extends TPaperCandidateChallengeAction,
>({
  challenges,
  candidateChallenge,
  additionalChallengeActions,
  disableCreateObjective,
  actions,
  actionsLoading,
  actionTypes,
  actionChannels,
  onActionClick,
  ...callbacks
}: IPaperCandidateChallengeProps<CandidateObjective, CandidateChallenge, Action>) => {
  const {
    currentChallenge,
    loading,
    createdFromCer,
    isEditMode,
    setIsEditMode,
    handleDeleteChallenge,
    handleEditChallenge,
    handleCreatObjective,
    handleEditObjective,
    handleDeleteObjective,
  } = usePaperCandidateChallenge(challenges, candidateChallenge, callbacks);

  if (!currentChallenge) {
    return (
      <Alert severity="error">
        <Typography variant="body1SemiBold">Oops !</Typography>
        <Typography variant="body1">Impossible de récupérer les informations de cette thématique.</Typography>
      </Alert>
    );
  }
  return (
    <PaperCandidateChallengeRoot className={paperCandidateChallengeClasses.root} variant="elevation" elevation={1}>
      <Stack className={paperCandidateChallengeClasses.header}>
        <Stack className={paperCandidateChallengeClasses.headerResponsiveContainer}>
          <Stack className={paperCandidateChallengeClasses.headerTitle}>
            <IconSVG icon={currentChallenge.icon} color="primary" />
            <Typography variant="h4" color="primary">
              {currentChallenge.name}
            </Typography>
          </Stack>
          <Stack className={paperCandidateChallengeClasses.headerActions}>{additionalChallengeActions}</Stack>
        </Stack>
        <Stack className={paperCandidateChallengeClasses.headerActions}>
          {!createdFromCer && (
            <>
              {callbacks.onEditChallenge && (
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => setIsEditMode((isEditMode) => !isEditMode)}
                  disabled={loading !== CandidateChallengeLoadingState.NONE}
                >
                  {loading === CandidateChallengeLoadingState.EDIT_CHALLENGE ? (
                    <CircularProgress size={24} />
                  ) : (
                    <EditOutlinedIcon />
                  )}
                </IconButton>
              )}
              <IconButton
                color="primary"
                size="small"
                onClick={handleDeleteChallenge}
                disabled={loading !== CandidateChallengeLoadingState.NONE}
              >
                {loading === CandidateChallengeLoadingState.DELETE_CHALLENGE ? (
                  <CircularProgress size={24} />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            </>
          )}
        </Stack>
      </Stack>
      {isEditMode ? (
        <PaperCandidateChallengeEditForm
          disabled={loading !== CandidateChallengeLoadingState.NONE}
          disableCreateObjective={disableCreateObjective}
          challenge={currentChallenge}
          candidateChallenge={candidateChallenge}
          onSubmit={handleEditChallenge}
          onCancel={() => setIsEditMode(false)}
        />
      ) : (
        <>
          {candidateChallenge.description && <Typography variant="body1">{candidateChallenge.description}</Typography>}
          <Stack className={paperCandidateChallengeClasses.objectivesList}>
            {candidateChallenge.objectives.map((candidateObjective, index) => (
              <PaperCandidateObjective
                // biome-ignore lint/suspicious/noArrayIndexKey: No other unique identifier available for objectives
                key={index}
                disabled={loading !== CandidateChallengeLoadingState.NONE}
                challenge={currentChallenge}
                candidateObjective={candidateObjective}
                onEdit={
                  callbacks.onEditObjective
                    ? (candidateObjective) => handleEditObjective(candidateObjective, index)
                    : undefined
                }
                onDelete={(candidateObjective) => handleDeleteObjective(candidateObjective, index)}
              />
            ))}
          </Stack>
          {!disableCreateObjective && (
            <PaperCreateCandidateObjective
              disabled={loading !== CandidateChallengeLoadingState.NONE}
              challenge={currentChallenge}
              onSubmit={handleCreatObjective}
            />
          )}
        </>
      )}
      {actionsLoading ? (
        <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
          <Typography variant="body1SemiBold">Actions liées</Typography>
          <CircularProgress size={14} color="primary" />
        </Stack>
      ) : (
        actions && (
          <PaperCandidateChallengeActions
            actions={actions}
            actionTypes={actionTypes}
            actionChannels={actionChannels}
            onActionClick={onActionClick}
          />
        )
      )}
    </PaperCandidateChallengeRoot>
  );
};
